import { useQuery } from "@apollo/client";
import { sortBy, uniqBy } from "lodash";
import { ReactNode, useEffect, useMemo, useState } from "react";
import {
  GetAllOrganizationTeamsQueryQuery,
  GetAllOrganizationTeamsQueryQueryVariables,
  GetCareerTrackPickerPeopleQuery,
  GetCareerTrackPickerPeopleQueryVariables,
  GetOrganizationCareerTracksQuery,
  GetOrganizationCareerTracksQueryVariables,
} from "types/graphql-schema";

import getAllOrganizationTeamsQuery from "@apps/artifact/graphql/get-all-organization-teams-query";
import getCareerTrackPickerPeopleQuery from "@apps/org-settings/graphql/get-career-track-picker-people-query";
import getOrganizationCareerTracksQuery from "@apps/org-settings/graphql/get-organization-career-tracks-query";
import useLabel from "@apps/use-label/use-label";
import Avatar from "@components/avatar/avatar";
import { ButtonSize } from "@components/button/button-constants";
import ComboboxGeneric from "@components/combobox/generic-combobox";
import { useLink } from "@components/link/link-helpers";
import { TopicflowIcon } from "@components/topicflow-icon/topicflow-icon";
import useDebounce from "@components/use-debounce/use-debounce";
import { assertEdgesNonNull } from "@helpers/helpers";

import {
  allCompetenciesPath,
  allRolesPath,
  careerTrackPath,
  departmentCompetenciesPath,
  personCareerPath,
} from "./helpers";

// What the picker can show, tagged by kind: ids of different kinds overlap.
const allRolesValue = "all-roles";
const allCompetenciesValue = "all-competencies";
const departmentValue = (teamId: number) => `team-${teamId}`;
const trackValue = (careerTrackId: number) => `track-${careerTrackId}`;
const personValue = (userId: number) => `person-${userId}`;

// An icon says what kind of thing each option is, in the list and in the
// field once picked: a globe for every track, a group for a department, one
// figure for a track, and a person's own face.
const optionLabel = (icon: ReactNode, text: string) => (
  <span className="flex items-center gap-2 min-w-0">
    <span className="flex shrink-0">{icon}</span>
    <span className="truncate">{text}</span>
  </span>
);
const iconClassName = "h-4 w-4";

// One "Viewing:" picker for what's being looked at: every track, one
// department's competencies, one career track, or one person.
const CareerTrackViewSwitcher = ({
  organizationId,
  careerTrackId,
  teamId,
  person,
  currentTrackTitle,
  showAllCompetencies,
  isOverview,
  onlyPeople,
  disabled,
}: {
  organizationId: number;
  careerTrackId?: number | null;
  teamId?: number;
  // The person being viewed, named here: they may not be among the people a
  // search has loaded.
  person?: { id: number; name: string; avatar?: string | null };
  // The track's name as it is on screen. The field shows it rather than the
  // list's copy: a track that isn't saved yet, or was just saved or renamed,
  // isn't in the list as it is now.
  currentTrackTitle?: string;
  // Viewing every competency rather than the company-wide ones.
  showAllCompetencies?: boolean;
  // On the Overview, which isn't one of the views, nothing is being viewed:
  // the picker is a search that opens a person's or a track's view.
  isOverview?: boolean;
  // The only people that can be picked, and nothing else: a manager's direct
  // reports, when tracks, departments and everyone else are admins' to view.
  onlyPeople?: ReadonlyArray<{
    id: number;
    name: string;
    avatar?: string | null;
  }>;
  disabled?: boolean;
}) => {
  const link = useLink();
  const label = useLabel();
  const [query, setQuery] = useState("");
  // People are searched on the server: wait for a pause in typing rather
  // than send a search per letter (the box itself updates straight away).
  const debouncedQuery = useDebounce(query, 300);
  const { data, loading, refetch } = useQuery<
    GetOrganizationCareerTracksQuery,
    GetOrganizationCareerTracksQueryVariables
  >(getOrganizationCareerTracksQuery, {
    fetchPolicy: "cache-and-network",
    variables: { organizationId },
    skip: !!onlyPeople,
  });
  // A track created since the list loaded isn't in it: load it again, once,
  // so the new track can be picked too.
  const isCurrentTrackMissing =
    !!careerTrackId &&
    !!data &&
    !assertEdgesNonNull(data.careerTracks).some(
      (track) => track.id === careerTrackId,
    );
  useEffect(() => {
    if (isCurrentTrackMissing) {
      refetch();
    }
  }, [isCurrentTrackMissing, refetch]);
  const { data: teamsData } = useQuery<
    GetAllOrganizationTeamsQueryQuery,
    GetAllOrganizationTeamsQueryQueryVariables
  >(getAllOrganizationTeamsQuery, {
    variables: { organizationId },
    skip: !!onlyPeople,
  });
  const { data: peopleData } = useQuery<
    GetCareerTrackPickerPeopleQuery,
    GetCareerTrackPickerPeopleQueryVariables
  >(getCareerTrackPickerPeopleQuery, {
    variables: { organizationId, search: debouncedQuery },
    skip: !!onlyPeople,
  });

  const options = useMemo(() => {
    if (onlyPeople) {
      return sortBy(
        uniqBy([...(person ? [person] : []), ...onlyPeople], "id"),
        (user) => user.name.toLowerCase(),
      ).map((user) => ({
        label: user.name,
        htmlLabel: optionLabel(<Avatar user={user} size="5" />, user.name),
        value: personValue(user.id),
        group: "People",
      }));
    }
    const tracks = data
      ? uniqBy(assertEdgesNonNull(data.careerTracks), "id")
      : [];
    const departments = teamsData?.organization
      ? assertEdgesNonNull(teamsData.organization.teams)
      : [];
    const people = uniqBy(
      [
        ...(person ? [person] : []),
        ...(peopleData?.organization
          ? assertEdgesNonNull(peopleData.organization.members).flatMap(
              (member) => (member.user ? [member.user] : []),
            )
          : []),
      ],
      "id",
    );
    const competenciesLabel = label("competency", {
      capitalize: true,
      pluralize: true,
    });
    const companyWideTitle = `Company-Wide ${competenciesLabel}`;
    const allCompetenciesTitle = `All ${competenciesLabel}`;
    // Views that gather competencies from many places, rather than one
    // department's, track's or person's.
    const multipleViewsGroup = `Multiple ${label("competency", {
      capitalize: true,
    })} Views`;
    return [
      {
        label: allCompetenciesTitle,
        htmlLabel: optionLabel(
          <TopicflowIcon type="layersTwo" className={iconClassName} />,
          allCompetenciesTitle,
        ),
        value: allCompetenciesValue,
        group: multipleViewsGroup,
      },
      {
        label: companyWideTitle,
        htmlLabel: optionLabel(
          <TopicflowIcon type="globe02" className={iconClassName} />,
          companyWideTitle,
        ),
        value: allRolesValue,
        group: multipleViewsGroup,
      },
      ...sortBy(departments, (team) => team.title.toLowerCase()).map(
        (team) => ({
          label: team.title,
          htmlLabel: optionLabel(
            <TopicflowIcon type="users" className={iconClassName} />,
            team.title,
          ),
          value: departmentValue(team.id),
          group: label("team", { capitalize: true, pluralize: true }),
        }),
      ),
      ...sortBy(tracks, (track) => track.title.toLowerCase()).map((track) => ({
        label: track.title,
        htmlLabel: optionLabel(
          <TopicflowIcon type="user" className={iconClassName} />,
          track.title,
        ),
        value: trackValue(track.id),
        group: "Career Tracks",
      })),
      ...sortBy(people, (user) => user.name.toLowerCase()).map((user) => ({
        label: user.name,
        htmlLabel: optionLabel(<Avatar user={user} size="5" />, user.name),
        value: personValue(user.id),
        group: "People",
      })),
    ];
  }, [data, label, onlyPeople, peopleData, person, teamsData]);

  const value = person
    ? personValue(person.id)
    : teamId
      ? departmentValue(teamId)
      : careerTrackId
        ? trackValue(careerTrackId)
        : showAllCompetencies
          ? allCompetenciesValue
          : allRolesValue;

  const selectedOption = isOverview
    ? {
        label: "Search",
        value: "search",
        htmlLabel: (
          <span className="flex items-center gap-2 min-w-0 font-normal text-gray-500">
            <TopicflowIcon type="search" className={iconClassName} />
            <span className="truncate">Search for a person or track</span>
          </span>
        ),
      }
    : currentTrackTitle !== undefined
      ? {
          label: currentTrackTitle,
          // An unsaved track has no id yet, and isn't any option.
          value: careerTrackId ? trackValue(careerTrackId) : "new",
          htmlLabel: optionLabel(
            <TopicflowIcon type="user" className={iconClassName} />,
            currentTrackTitle,
          ),
        }
      : (options.find((option) => option.value === value) ?? null);

  return (
    <div className="flex items-center gap-2 min-w-0">
      {/* The icon says the kind, so "Viewing:" and a name fit; longer names
          truncate. */}
      <div className="w-72 min-w-0">
        <ComboboxGeneric
          aria-label="Career track switcher"
          prefix={isOverview ? undefined : "Viewing"}
          // A shaded band per group, so Departments, Career Tracks and People
          // read as sections of the list.
          groupHeaderClassName="bg-gray-100 text-gray-500 pt-1.5 pb-1.5"
          buttonProps={{ size: ButtonSize.medium }}
          disabled={disabled}
          loading={loading}
          options={options}
          value={selectedOption}
          query={query}
          setQuery={setQuery}
          searchPlaceholder="Search..."
          onChangeValue={(option) => {
            if (option.value === selectedOption?.value) {
              return;
            }
            if (option.value === allCompetenciesValue) {
              link.redirect(allCompetenciesPath(organizationId));
              return;
            }
            const [kind, id] = option.value.split("-");
            if (kind === "team") {
              link.redirect(departmentCompetenciesPath(organizationId, +id));
            } else if (kind === "track") {
              link.redirect(careerTrackPath(organizationId, +id));
            } else if (kind === "person") {
              link.redirect(personCareerPath(organizationId, +id));
            } else {
              link.redirect(allRolesPath(organizationId));
            }
          }}
        />
      </div>
    </div>
  );
};

export default CareerTrackViewSwitcher;
